import React from "react";
import { Table, Button, Card, Typography, Space, Tag, Row, Col } from "antd";
import { DeleteOutlined } from "@ant-design/icons";

import { Transaction } from "../types";
import { currencyFormatter } from "../utils/helpers";

const { Title, Paragraph, Text } = Typography;

interface TransactionsTableProps {
  transactions: Transaction[];
  selectedRowIds: number[];
  onSelectChange: (selectedRowIds: number[]) => void;
  onRemoveSelected: () => void;
  onRemoveTransaction: (id: number) => void;
  onClear: () => void;
  onSubmit: () => void;
  loading: boolean;
}

const TransactionsTable: React.FC<TransactionsTableProps> = ({
  transactions,
  selectedRowIds,
  onSelectChange,
  onRemoveSelected,
  onClear,
  onSubmit,
  loading,
}) => {
  const columns = [
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (amount: number) => (
        <Text strong style={{ color: amount >= 0 ? "green" : "red" }}>
          {currencyFormatter(amount)}
        </Text>
      ),
    },
    {
      title: "Paid By",
      dataIndex: "senderName",
      key: "senderName",
    },
    {
      title: "IBAN",
      dataIndex: "iban",
      key: "iban",
    },
    {
      title: "Description",
      dataIndex: "purpose",
      key: "purpose",
    },
    {
      title: "Payment Date",
      dataIndex: "bookingDate",
      key: "bookingDate",
    },
  ];

  const rowSelection = {
    selectedRowKeys: selectedRowIds,
    onChange: (selectedRowKeys: React.Key[]) => {
      onSelectChange(selectedRowKeys as number[]);
    },
  };

  const hasSelected = selectedRowIds.length > 0;

  return (
    <Card>
      <Title level={3}>Generated Transactions Preview</Title>
      <Paragraph>
        Review the transactions before committing them to the system. You can
        remove individual transactions or select multiple to remove.
      </Paragraph>

      <div style={{ marginBottom: 16 }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Space>
              <Button
                danger
                onClick={onRemoveSelected}
                disabled={!hasSelected}
                icon={<DeleteOutlined />}
              >
                Remove Selected
              </Button>
              <span>
                {hasSelected && (
                  <Text type="secondary">
                    {`Selected ${selectedRowIds.length} items`}
                  </Text>
                )}
              </span>
            </Space>
          </Col>
          <Col>
            {transactions.length > 0 && (
              <Tag color="blue">Total: {transactions.length} transactions</Tag>
            )}
          </Col>
        </Row>
      </div>

      <Table
        rowSelection={rowSelection}
        columns={columns}
        dataSource={transactions}
        rowKey="id"
        pagination={false}
        scroll={{ y: 400 }}
        locale={{ emptyText: "No transactions generated yet" }}
      />

      <Row justify="end" style={{ marginTop: 16 }}>
        <Space>
          <Button onClick={onClear} disabled={transactions.length === 0}>
            Clear
          </Button>
          <Button
            type="primary"
            onClick={onSubmit}
            disabled={transactions.length === 0}
            loading={loading}
          >
            Submit Transactions
          </Button>
        </Space>
      </Row>
    </Card>
  );
};

export default TransactionsTable;
